/*
 * Tyee Archery — Canadian counter-tariff surcharge (cart reconciler)
 * ------------------------------------------------------------------
 * WHAT: every product that is NOT tagged `tariff-free` carries a surcharge
 *       (default 50% of the regular unit price) that must appear at checkout
 *       as its own visible line. Shopify (Grow plan, no Functions) cannot add
 *       fees on its own, so the fee is a real product — "Canadian Counter-
 *       Tariff Surcharge" — with one variant per fee amount. This module keeps
 *       the cart honest: for every exposed line it makes sure exactly the right
 *       fee line exists, with the right amount and the same quantity.
 *
 * RULE (single source of truth, mirrored in the Liquid note and the Flow
 * audit):  rate = 0   if the product is a gift card or tagged `tariff-free`
 *          rate = NN  if the product is tagged `tariff-rate-NN`
 *          rate = default (config `rate`, 50) otherwise
 *          fee per unit = floor((unit price in cents × rate + 50) / 100)
 *
 * TINKER 2.1 (Tariff-Free Zone): units on our shelves came in before the tariff, so the fee is
 * charged only on quantity BEYOND Shopify stock for that variant (read from
 * /products/<handle>?view=tyee-stock, cached 60 s). Lines added from the Tariff-Free Zone carry the
 * hidden property _tyee_zone and are in-stock units only: their quantity is capped at Shopify stock.
 * If the stock lookup fails nothing is capped and regular units are charged in full (money-safe).
 *
 * HOW: after ANY cart mutation (theme events, fetch to /cart/*, page show,
 * tab focus) it reads /cart.js, works out the expected fee lines, and repairs
 * the difference with /cart/update.js + /cart/add.js. Fee lines are linked to
 * their parent by the hidden line property `_tariff_for` (= parent variant id).
 * Orphan or hand-added fee lines are removed. Checkout is intercepted and the
 * cart re-verified before the customer is let through.
 *
 * SAFETY NET: this runs in the browser and can be bypassed by someone driving
 * the cart API directly, so every order is ALSO audited server-side by the
 * Shopify Flow "Tariff surcharge audit" (see Downloads/tyee-tariff/flow/).
 *
 * CONFIG: <script id="tyee-tariff-config" type="application/json"> rendered by
 * snippets/tyee-tariff-config.liquid from the shop metafield tyee.tariff_map,
 * which tariff-sync.py maintains ({rate, product_id, handle, amounts{cents: variantId}}).
 */
import { CartLinesUpdateEvent } from 'https://cdn.shopify.com/storefront/standard-events.js';

const PROP_FOR = '_tariff_for';
const PROP_RATE = '_tariff_rate';
const PROP_PART = '_tariff_part';
const PROP_LABEL = 'Applied to';
const PROP_ZONE = '_tyee_zone';
const DENOMS = [100000, 10000, 2000, 500, 100, 25, 5, 1];
const DEBUG = /[?&]tyee_debug=1/.test(location.search) || localStorage.getItem('tyee_tariff_debug') === '1';

const log = (...a) => { if (DEBUG) console.log('[tyee-tariff]', ...a); };
const warn = (...a) => console.warn('[tyee-tariff]', ...a);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/* ---------- config ---------- */
const CFG = (() => {
  const el = document.getElementById('tyee-tariff-config');
  if (!el) return null;
  try {
    const c = JSON.parse(el.textContent || '{}');
    if (!c || typeof c !== 'object' || !c.product_id || !c.amounts) return null;
    c.product_id = Number(c.product_id);
    c.rate = Number(c.rate) || 50;
    c.tag_free = c.tag_free || 'tariff-free';
    c.tag_fee = c.tag_fee || 'tyee-surcharge';
    c.currency = el.dataset.currency || 'CAD';
    c.enabled = c.enabled !== false;
    return c;
  } catch (e) {
    warn('config unreadable', e);
    return null;
  }
})();

const state = {
  running: null,
  queued: false,
  lastOkSig: '',
  lastOkAt: 0,
  unfixable: [],
  capped: [],
  busy: false,
};
window.TyeeTariff = { config: CFG, state, reconcile: () => reconcile('manual'), version: '2.1.1' };

if (!CFG) {
  warn('no usable config — surcharge reconciler is OFF. Orders will be flagged by the Flow audit.');
} else if (!CFG.enabled) {
  warn('config.enabled=false — surcharge reconciler is OFF.');
}

/* ---------- helpers ---------- */
async function fetchJSON(url, opts = {}) {
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...opts,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Tyee-Tariff': '1',
      ...(opts.headers || {}),
    },
  });
  const text = await res.text();
  let json = null;
  try { json = JSON.parse(text); } catch (e) { /* not json */ }
  if (!res.ok) {
    const err = new Error(`${url} -> HTTP ${res.status}`);
    err.body = json || text;
    throw err;
  }
  return json;
}

function rateFromTags(tags) {
  if (!Array.isArray(tags)) return CFG.rate; // unknown -> charge (money-safe)
  const lower = tags.map((t) => String(t).trim().toLowerCase());
  if (lower.includes(CFG.tag_fee.toLowerCase())) return null; // the fee product itself
  if (lower.includes(CFG.tag_free.toLowerCase())) return 0;
  for (const t of lower) {
    const m = /^tariff-rate-(\d{1,3})$/.exec(t);
    if (m) return Math.min(999, parseInt(m[1], 10));
  }
  return CFG.rate;
}

function feeCents(unitCents, rate) {
  return Math.floor((unitCents * rate + 50) / 100);
}

function money(cents) {
  try {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: CFG.currency }).format(cents / 100);
  } catch (e) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/* product info (tags) cache: memory + sessionStorage, 10 minutes */
const infoCache = new Map();
function ssRead(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || o.exp < Date.now()) return null;
    return o.v;
  } catch (e) { return null; }
}
function ssWrite(key, v, ttl) {
  try { sessionStorage.setItem(key, JSON.stringify({ v, exp: Date.now() + ttl })); } catch (e) { /* quota */ }
}
function productInfo(handle) {
  if (infoCache.has(handle)) return infoCache.get(handle);
  const p = (async () => {
    const cached = ssRead(`tyee_tariff21:p:${handle}`);
    if (cached) return cached;
    try {
      const j = await fetchJSON(`/products/${encodeURIComponent(handle)}.js`);
      let onhand = null;
      try {
        // NOT fetchJSON: with Accept: application/json Shopify ignores ?view= and serves product JSON
        const res = await fetch(`/products/${encodeURIComponent(handle)}?view=tyee-stock`, { credentials: 'same-origin', headers: { Accept: 'text/html', 'X-Tyee-Tariff': '1' } });
        if (!res.ok) throw new Error(`stock view HTTP ${res.status}`);
        const s = JSON.parse(await res.text());
        onhand = {};
        for (const [vid, q] of Object.entries((s && s.onhand) || {})) onhand[vid] = Math.max(0, Number(q) || 0);
      } catch (e) {
        warn('stock lookup failed: nothing capped, regular units charged in full', handle, e.message);
      }
      const info = {
        tags: Array.isArray(j.tags) ? j.tags : [],
        title: j.title,
        variants: (j.variants || []).map((v) => ({ id: v.id, price: v.price, title: v.title })),
        onhand,
      };
      // short TTL: stock moves with every sale
      ssWrite(`tyee_tariff21:p:${handle}`, info, 60 * 1000);
      return info;
    } catch (e) {
      warn('product lookup failed, defaulting to full rate', handle, e.message);
      return { tags: null, unknown: true };
    }
  })();
  infoCache.set(handle, p);
  p.then((info) => { if (info.unknown) infoCache.delete(handle); }, () => infoCache.delete(handle));
  return p;
}

function isFeeItem(it) {
  return Number(it.product_id) === CFG.product_id || it.handle === CFG.handle;
}

function labelFor(it) {
  const vt = it.variant_title && it.variant_title !== 'Default Title' ? ` – ${it.variant_title}` : '';
  return `${it.product_title}${vt}`.slice(0, 250);
}

function cartSig(cart) {
  return cart.items.map((i) => `${i.key}:${i.quantity}:${i.price}`).sort().join('|');
}

function nonFeeCount(cart) {
  return cart.items.reduce((n, i) => n + (isFeeItem(i) ? 0 : i.quantity), 0);
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
  return Array.from(ids);
}

function decompose(cents) {
  const parts = [];
  let left = cents;
  for (const d of DENOMS) {
    if (!CFG.amounts[String(d)]) continue;
    const n = Math.floor(left / d);
    if (n > 0) { parts.push([d, n]); left -= n * d; }
  }
  return left === 0 && parts.length ? parts : null;
}

/* ---------- planning ---------- */
async function buildPlan(cart) {
  const feeLines = [];
  const productLines = [];
  for (const it of cart.items) (isFeeItem(it) ? feeLines : productLines).push(it);

  // expected fee per parent variant
  const ops = { updates: {}, adds: [] };
  const expected = new Map();
  for (const it of productLines) {
    let rate = 0;
    let info = null;
    if (!it.gift_card) {
      info = await productInfo(it.handle);
      rate = rateFromTags(info.tags);
    }
    if (rate === null || rate <= 0) continue;
    const fc = feeCents(it.price, rate);
    if (fc <= 0) continue;
    const vid = Number(it.variant_id);
    const known = !!(info && info.onhand);
    const avail = known ? info.onhand[String(vid)] || 0 : 0;
    const e = expected.get(vid) || { feeCents: fc, qty: 0, zone: 0, zoneLines: [], known, avail, rate, label: labelFor(it), title: it.product_title };
    if (it.properties && it.properties[PROP_ZONE]) { e.zone += it.quantity; e.zoneLines.push(it); }
    else e.qty += it.quantity;
    expected.set(vid, e);
  }
  // Tinker 2.1: shelf stock is pre-tariff. Zone lines never exceed Shopify stock; regular units are
  // charged only beyond what is left on the shelf after the zone lines.
  for (const [vid, e] of expected) {
    if (!e.known) {
      if (e.qty === 0) expected.delete(vid);
      continue;
    }
    let over = e.zone - e.avail;
    for (let i = e.zoneLines.length - 1; over > 0 && i >= 0; i--) {
      const ln = e.zoneLines[i];
      const cut = Math.min(over, ln.quantity);
      ops.updates[ln.key] = ln.quantity - cut;
      over -= cut;
    }
    if (e.zone > e.avail) state.capped.push({ title: e.title, avail: e.avail });
    const zoneKept = Math.min(e.zone, e.avail);
    e.qty = Math.max(0, e.qty - Math.max(0, e.avail - zoneKept));
    if (e.qty === 0) expected.delete(vid);
  }

  // actual fee lines grouped by parent
  const actual = new Map();
  for (const f of feeLines) {
    const forId = f.properties && f.properties[PROP_FOR] != null ? Number(f.properties[PROP_FOR]) : 0;
    if (!forId || !expected.has(forId)) {
      ops.updates[f.key] = 0; // orphan / hand-added / parent removed or now exempt
      continue;
    }
    const list = actual.get(forId) || [];
    list.push(f);
    actual.set(forId, list);
  }

  state.unfixable = [];
  for (const [vid, e] of expected) {
    const lines = actual.get(vid) || [];
    const want = e.feeCents * e.qty;
    const total = lines.reduce((s, f) => s + f.price * f.quantity, 0);
    const canonical = lines.length === 1 && lines[0].price === e.feeCents && !(lines[0].properties || {})[PROP_PART];
    if (canonical) {
      if (lines[0].quantity !== e.qty) ops.updates[lines[0].key] = e.qty;
      continue;
    }
    const denominated = lines.length > 0 && total === want && lines.every((f) => (f.properties || {})[PROP_PART]);
    if (denominated) continue;
    for (const f of lines) ops.updates[f.key] = 0;
    const props = { [PROP_FOR]: String(vid), [PROP_RATE]: String(e.rate), [PROP_LABEL]: e.label };
    const exact = CFG.amounts[String(e.feeCents)];
    if (exact) {
      ops.adds.push({ id: Number(exact), quantity: e.qty, properties: props });
    } else {
      const parts = decompose(e.feeCents);
      if (!parts) {
        state.unfixable.push({ variant: vid, feeCents: e.feeCents });
        warn('no fee variant for amount', e.feeCents, 'variant', vid);
        continue;
      }
      for (const [cents, n] of parts) {
        ops.adds.push({ id: Number(CFG.amounts[String(cents)]), quantity: n * e.qty, properties: { ...props, [PROP_PART]: String(cents) } });
      }
    }
  }
  return ops;
}

async function applyPlan(ops) {
  const ids = sectionIds();
  const hasUpd = Object.keys(ops.updates).length > 0;
  const hasAdd = ops.adds.length > 0;
  let last = null;
  if (hasUpd) {
    log('update', ops.updates);
    last = await fetchJSON('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ updates: ops.updates, ...(hasAdd ? {} : { sections: ids.join(','), sections_url: location.pathname }) }),
    });
  }
  if (hasAdd) {
    log('add', ops.adds);
    last = await fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ items: ops.adds, sections: ids.join(','), sections_url: location.pathname }),
    });
  }
  return last && last.sections ? last.sections : null;
}

function announce(cart, sections) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const ev = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise });
  ev.tyeeTariff = true;
  document.dispatchEvent(ev);
  const count = nonFeeCount(cart);
  const base = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
  deferred.resolve({
    cart: { ...base, totalQuantity: count },
    detail: { sections: sections || undefined, items: cart.items, itemCount: count, source: 'tyee-tariff', didError: false },
  });
}

/* ---------- reconcile (serialised) ---------- */
function reconcile(reason) {
  if (!CFG || !CFG.enabled) return Promise.resolve(false);
  if (state.running) {
    state.queued = true;
    return state.running;
  }
  state.running = (async () => {
    let changed = false;
    let sections = null;
    let cart = null;
    state.capped = [];
    for (let pass = 0; pass < 4; pass++) {
      cart = await fetchJSON('/cart.js');
      const ops = await buildPlan(cart);
      const n = Object.keys(ops.updates).length + ops.adds.length;
      log(`reconcile(${reason}) pass ${pass}: ${n} op(s)`);
      if (n === 0) break;
      changed = true;
      sections = await applyPlan(ops);
    }
    if (cart) {
      if (changed) {
        cart = await fetchJSON('/cart.js');
        announce(cart, sections);
      }
      if (state.capped.length) toast(state.capped);
      if (!state.unfixable.length) {
        state.lastOkSig = cartSig(cart);
        state.lastOkAt = Date.now();
      }
    }
    return changed;
  })()
    .catch((e) => { warn('reconcile failed', e); return false; })
    .finally(() => {
      state.running = null;
      if (state.queued) {
        state.queued = false;
        reconcile('queued');
      }
    });
  return state.running;
}

function toast(list) {
  const msg = list.map((c) => (c.avail > 0
    ? `Only ${c.avail} of ${c.title} ${c.avail === 1 ? 'is' : 'are'} on our shelves right now, so your Tariff-Free Zone quantity was adjusted.`
    : `${c.title} just sold out of the Tariff-Free Zone and was taken out of your cart.`)).join(' ');
  let el = document.getElementById('tyee-zone-toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'tyee-zone-toast';
    el.setAttribute('role', 'status');
    el.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483000;max-width:min(92vw,560px);padding:14px 18px;border-radius:10px;background:#12160e;color:#ece4d2;border:2px solid #e8621a;font:600 14px/1.45 system-ui,sans-serif;box-shadow:0 10px 30px rgb(0 0 0/.35)';
    document.body.appendChild(el);
  }
  el.textContent = msg;
  clearTimeout(el._t);
  el._t = setTimeout(() => el.remove(), 7000);
}

let debounceTimer = 0;
function schedule(reason, delay = 60) {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => reconcile(reason), delay);
}

/* ---------- triggers ---------- */
if (CFG && CFG.enabled) {
  // 1. theme + app events (product form, quick add, cart items, quick order list)
  document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
    if (event.tyeeTariff) return;
    const p = event.promise;
    if (p && typeof p.then === 'function') {
      p.then(() => schedule('event'), () => schedule('event-error'));
    } else {
      schedule('event');
    }
  });

  // 2. anything else that talks to the cart endpoints (apps, inline scripts)
  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url ? input.url : '';
    const mine = init && init.headers && (init.headers['X-Tyee-Tariff'] || (init.headers.get && init.headers.get('X-Tyee-Tariff')));
    const res = origFetch.apply(this, arguments);
    if (!mine && /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.test(url)) {
      res.then(() => schedule('fetch', 150), () => {});
    }
    return res;
  };
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (/\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.test(String(url))) {
      this.addEventListener('loadend', () => schedule('xhr', 150));
    }
    return origOpen.apply(this, arguments);
  };
  // classic form posts to /cart/add (no-JS fallback) land on a new page; covered by load below.

  // 3. page lifecycle
  const onLoad = () => reconcile('load');
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onLoad); else onLoad();
  window.addEventListener('pageshow', (e) => { if (e.persisted) reconcile('pageshow'); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - state.lastOkAt > 30000) reconcile('visible');
  });

  // 4. checkout guard — re-verify right before leaving for checkout
  let bypass = false;
  function setBusy(on) {
    state.busy = on;
    document.querySelectorAll('#checkout, [name="checkout"], .cart__checkout-button').forEach((b) => {
      if (on) { b.setAttribute('aria-busy', 'true'); b.dataset.tyeeWasDisabled = b.disabled ? '1' : ''; b.disabled = true; }
      else { b.removeAttribute('aria-busy'); if (!b.dataset.tyeeWasDisabled) b.disabled = false; }
    });
  }
  async function verifyThen(go) {
    setBusy(true);
    try {
      await Promise.race([reconcile('checkout'), sleep(6000)]);
    } catch (e) { warn('checkout verify failed', e); }
    finally { setBusy(false); }
    if (state.unfixable.length) {
      alert('We could not apply the Canadian counter-tariff surcharge to an item in your cart. Please refresh the page and try again, or contact us and we will complete the order for you.');
      return;
    }
    bypass = true;
    try { go(); } finally { setTimeout(() => { bypass = false; }, 0); }
  }
  document.addEventListener('submit', (e) => {
    if (bypass) return;
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = e.submitter;
    const toCheckout = (submitter && submitter.name === 'checkout') || /\/checkout(\?|$)/.test(form.getAttribute('action') || '');
    if (!toCheckout) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    verifyThen(() => (submitter ? form.requestSubmit(submitter) : form.requestSubmit()));
  }, true);
  document.addEventListener('click', (e) => {
    if (bypass) return;
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (!/^(https?:\/\/[^/]+)?\/checkout(\?|$)/.test(href)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    verifyThen(() => { window.location.href = a.href; });
  }, true);

  // 5. product page: no "Buy it now" (it skips the cart and therefore the surcharge)
  const DYN = 'product-form-component .shopify-payment-button, product-form-component shopify-accelerated-checkout, .product-form-buttons .shopify-payment-button, .product-form-buttons shopify-accelerated-checkout';
  const strip = (root) => (root || document).querySelectorAll(DYN).forEach((el) => el.remove());
  strip();
  new MutationObserver((muts) => { for (const m of muts) if (m.addedNodes.length) { strip(); return; } })
    .observe(document.documentElement, { childList: true, subtree: true });

  // 6. product page note: keep the "+ $X" amount in step with the selected variant
  async function refreshNotes() {
    const notes = document.querySelectorAll('[data-tyee-tariff-note]');
    if (!notes.length) return;
    for (const note of notes) {
      const handle = note.dataset.handle;
      const rate = Number(note.dataset.rate) || CFG.rate;
      const out = note.querySelector('[data-tyee-fee]');
      if (!handle || !out) continue;
      const scope = note.closest('product-information, .product-information, main') || document;
      const input = scope.querySelector('product-form-component input[name="id"], input[name="id"]');
      const vid = input ? Number(input.value) : 0;
      const info = await productInfo(handle);
      const v = info.variants ? info.variants.find((x) => x.id === vid) || info.variants[0] : null;
      if (v) out.textContent = money(feeCents(v.price, rate));
      const oh = v && info.onhand ? Number(info.onhand[String(v.id)]) || 0 : 0;
      note.classList.toggle('is-waived', oh > 0);
      note.querySelectorAll('[data-tyee-oh]').forEach((el) => { el.textContent = String(oh); });
      note.querySelectorAll('[data-tyee-fee]').forEach((el) => { if (v) el.textContent = money(feeCents(v.price, rate)); });
      const allin = note.querySelector('[data-tyee-allin]');
      if (v && allin) allin.textContent = money(v.price + feeCents(v.price, rate));
    }
  }
  refreshNotes();
  document.addEventListener('change', () => setTimeout(refreshNotes, 50));
  document.addEventListener('shopify:product:select', () => setTimeout(refreshNotes, 50));
  document.addEventListener('variant:update', () => setTimeout(refreshNotes, 50));
}
